import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, ViewChild, inject } from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperInstance, ChartWrapperService } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';

export interface CustomerPricingRow {
  customerId: string;
  customerName: string;
  region: string;
  customerType: string;
  equipment: string;
  engine: string;
  serialNumber?: string;
  salesQuantity: number;
  listPrice: number;
  transactionPrice: number;
  currentVolume: number;
  futureVolume: number;
  projectedSales: number;
  date?: string;
  [key: string]: any;
}

export interface CustomerPricingPoint extends CustomerPricingRow {
  customerDiscountPct: number;
  opportunityValue: number;
  priceGapVsRegression: number;
  potentialUplift: number;
  distinctSerialCount: number;
  isOpportunity: boolean;
}

@Component({
  selector: 'app-customer-pricing-dashboard',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './customer-pricing-dashboard.component.html',
  styleUrls: ['./customer-pricing-dashboard.component.scss'],
})
export class CustomerPricingDashboardComponent implements AfterViewInit, OnDestroy {
  private widgetData!: any;

  @Input() set data(value: any) {
    this.widgetData = value;
    this.initializeFilterState();
  }

  get data(): any {
    return this.widgetData;
  }
  private chartHostRef?: ElementRef<HTMLDivElement>;

  @ViewChild('chartHost') set chartHost(value: ElementRef<HTMLDivElement> | undefined) {
    if (value && this.myChart && this.myChart.getDom() !== value.nativeElement) {
      this.myChart.dispose();
      this.myChart = undefined;
      this.resizeObserver?.disconnect();
      this.resizeObserver = undefined;
    }
    this.chartHostRef = value;
    if (value && !this.loading) {
      requestAnimationFrame(() => this.renderChart());
    }
  }

  get chartHost(): ElementRef<HTMLDivElement> | undefined {
    return this.chartHostRef;
  }

  rows: CustomerPricingRow[] = [];
  points: CustomerPricingPoint[] = [];
  filteredPoints: CustomerPricingPoint[] = [];
  selectedCustomer?: CustomerPricingPoint;
  selectedOpportunityCustomerId = '';
  legendItems: Array<{ label: string; color: string }> = [];
  selectedLegendCategory = '';
  loading = true;
  error = '';
  isCustomerLevel = false;

  protected myChart?: echarts.ECharts;
  protected allSeriesNames: string[] = [];
  protected allCategories: string[] = [];
  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();

  private configService = inject(ConfigService);
  private chartFilterHandler = inject(ChartFilterHandlerService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private resizeObserver?: ResizeObserver;
  private resizeRafId: number | null = null;

  ngAfterViewInit(): void {
    this.wrapper.attach(this.data, {
      onReload: () => this.loadData(),
      getChart: () => this.myChart || null,
      getSeriesNames: () => this.allSeriesNames,
      getCategories: () => this.allCategories,
    });

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => this.myChart?.resize());
    this.themeSub = this.themeService.theme$.subscribe(() => this.renderChart());
    this.loadData();
  }

  ngOnDestroy(): void {
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    if (this.resizeRafId !== null) {
      cancelAnimationFrame(this.resizeRafId);
    }
    this.wrapper.detach();
    this.myChart?.dispose();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.myChart?.resize();
  }

  get cfg(): any {
    return this.data?.dashboardConfig || this.data?.chartConfig || {};
  }

  get chartHeight(): string {
    return this.data?.height || this.cfg?.customerOverview?.height || '';
  }

  loadData(): void {
    this.loading = true;
    this.error = '';

    this.configService.getConfig(this.data?.apiUrl, this.data)?.subscribe({
      next: (res: any) => {
        const dataKey = this.data?.dataKey || this.cfg?.dataSource?.dataKey || 'customers';
        const source = this.extractRows(res, dataKey);
        this.rows = source.map((row: any) => this.normalizeRow(row));
        this.points = this.buildPoints(this.rows);
        this.loading = false;
        this.showRegionLevel();
      },
      error: () => {
        this.error = this.cfg?.states?.errorText || 'Unable to load customer pricing data.';
        this.loading = false;
      },
    });
  }

  showRegionLevel(): void {
    this.isCustomerLevel = false;
    this.filteredPoints = [...this.points];
    if (!this.selectedCustomer || !this.filteredPoints.some((p) => p.customerId === this.selectedCustomer?.customerId)) {
      this.selectedCustomer = this.filteredPoints[0];
    }
    requestAnimationFrame(() => this.renderChart());
  }

  viewAtCustomerLevel(): void {
    this.isCustomerLevel = true;
    if (this.selectedCustomer) {
      this.filteredPoints = this.points.filter((point) => point.customerId === this.selectedCustomer?.customerId);
      requestAnimationFrame(() => this.renderChart());
    }
  }

  selectCustomer(customer: CustomerPricingPoint): void {
    this.selectedCustomer = customer;
    this.selectedOpportunityCustomerId =
      this.selectedOpportunityCustomerId === customer.customerId ? '' : customer.customerId;
    this.renderChart();
  }

  protected renderChart(): void {
    if (!this.chartHost?.nativeElement || this.loading) return;
    this.ensureResizeObserver();

    if (!this.myChart) {
      this.myChart = echarts.init(this.chartHost.nativeElement, 'dvm');
    }

    const config = this.cfg?.customerOverview || {};
    const responsive = this.resolveResponsiveChartConfig(config);
    const threshold = config.thresholdLine || {};
    const xMetric = config.axes?.x?.metric || 'distinctSerialCount';
    const yMetric = config.axes?.y?.metric || 'customerDiscountPct';
    const allLegendCategories = this.unique(this.filteredPoints.map((point) => point.customerType));
    this.legendItems = allLegendCategories.map((category, index) => ({
      label: category,
      color: this.resolveColor(category, index),
    }));
    const categories = this.selectedLegendCategory
      ? allLegendCategories.filter((category) => category === this.selectedLegendCategory)
      : allLegendCategories;
    const maxX = Math.max(config.axes?.x?.max ?? 0, ...this.filteredPoints.map((point) => Number(point[xMetric] || 0)), 1);
    const maxY = Math.max(config.axes?.y?.max ?? 0, ...this.filteredPoints.map((point) => Number(point[yMetric] || 0)), 1);
    const minY = config.axes?.y?.min ?? 0;
    const xInterval = config.axes?.x?.interval === 'auto'
      ? undefined
      : maxX > 50 ? Math.ceil(maxX / 6) : config.axes?.x?.interval;
    const thresholdData = [
      [0, this.thresholdY(0, 0, maxX)],
      [maxX, this.thresholdY(maxX, 0, maxX)],
    ];
    const dataZoom = config.dataZoom || {};
    const showDataZoom = dataZoom.show === true;
    const currentZoom = this.resolveCurrentZoom(dataZoom);

    this.allSeriesNames = categories;
    this.allCategories = this.filteredPoints.map((point) => point.customerName);
    this.wrapper.setSourceData(this.filteredPoints, 'customerName', 'customerType');

    const series: any[] = [
      {
        name: config.zones?.upper?.label || 'Opportunity',
        type: 'line',
        silent: true,
        symbol: 'none',
        lineStyle: { opacity: 0 },
        data: [
          [0, maxY],
          [maxX, maxY],
        ],
        areaStyle: { color: config.zones?.upper?.color || 'rgba(248, 113, 113, 0.35)' },
        z: 0,
      },
      {
        name: threshold.label || 'Threshold',
        type: 'line',
        silent: true,
        symbol: 'none',
        lineStyle: {
          color: threshold.color || '#111827',
          width: threshold.width || 2,
          type: threshold.style || 'solid',
        },
        data: thresholdData,
        areaStyle: { color: config.zones?.lower?.color || 'rgba(190, 242, 100, 0.45)' },
        z: 1,
        markPoint: this.buildOpportunityBadge(config),
      },
      ...categories.map((category, index) => this.buildScatterSeries(category, index, xMetric, yMetric, config)),
    ];

    const option: any = {
      color: categories.map((category, index) => this.resolveColor(category, index)),
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => this.tooltip(params.data, config),
      },
      legend: {
        show: false,
        top: responsive.legendTop,
        itemWidth: responsive.legendItemSize,
        itemHeight: responsive.legendItemSize,
        itemGap: responsive.legendGap,
        textStyle: {
          fontSize: responsive.legendFontSize,
        },
        data: categories,
      },
      grid: {
        ...responsive.grid,
        bottom: showDataZoom
          ? Number(dataZoom.gridBottom ?? Math.max(Number(responsive.grid.bottom || 0), 72))
          : responsive.grid.bottom,
      },
      xAxis: {
        type: 'value',
        name: config.axes?.x?.label,
        min: config.axes?.x?.min ?? 0,
        max: maxX,
        interval: xInterval,
        nameLocation: 'middle',
        nameGap: responsive.xAxisNameGap,
        nameTextStyle: {
          fontSize: responsive.axisNameFontSize,
        },
        axisLabel: {
          fontSize: responsive.axisLabelFontSize,
        },
        splitLine: { show: config.axes?.x?.splitLine !== false },
      },
      yAxis: {
        type: 'value',
        name: config.axes?.y?.label,
        min: minY,
        max: maxY,
        interval: config.axes?.y?.interval,
        nameTextStyle: {
          fontSize: responsive.axisNameFontSize,
        },
        axisLabel: {
          formatter: `{value}${config.axes?.y?.suffix || ''}`,
          fontSize: responsive.axisLabelFontSize,
        },
      },
      dataZoom: showDataZoom
        ? [
            {
              type: 'inside',
              xAxisIndex: 0,
              filterMode: 'none',
              zoomOnMouseWheel: true,
              moveOnMouseMove: true,
              start: currentZoom.start,
              end: currentZoom.end,
            },
            {
              type: 'slider',
              xAxisIndex: 0,
              filterMode: 'none',
              height: Number(dataZoom.height ?? 18),
              bottom: Number(dataZoom.bottom ?? 8),
              start: currentZoom.start,
              end: currentZoom.end,
            },
          ]
        : undefined,
      series,
    };

    this.myChart.setOption(option, true);
    this.wrapper.afterRender();
    this.myChart.off('click');
    this.myChart.on('click', (params: any) => {
      if (params?.data?.customerId) {
        const clickedPoint = params.data as CustomerPricingPoint;
        this.selectCustomer(clickedPoint);
        this.chartFilterHandler.updateFilterSelection(
          this.data,
          { customer_name: clickedPoint.customerName },
          clickedPoint,
        );
      }
    });
  }

  private buildScatterSeries(category: string, index: number, xMetric: string, yMetric: string, config: any): any {
    const responsive = this.resolveResponsiveChartConfig(config);
    const color = this.resolveColor(category, index);
    const points = this.filteredPoints
      .filter((point) => point.customerType === category)
      .map((point, pointIndex) => {
        const chartPoint = {
          ...point,
          name: point.customerName,
          value: [Number(point[xMetric] || 0), Number(point[yMetric] || 0)],
          itemStyle: {
            color,
            borderColor: point.customerId === this.selectedCustomer?.customerId ? config.selectedPoint?.borderColor || '#111827' : color,
            borderWidth: point.customerId === this.selectedCustomer?.customerId ? config.selectedPoint?.borderWidth || 3 : 1,
          },
        };

        return {
          ...chartPoint,
          label: this.buildPointLabel(chartPoint, pointIndex, xMetric, yMetric, responsive, config),
        };
      });

    return {
      name: category,
      type: 'scatter',
      data: points,
      symbol: config.point?.symbol || 'circle',
      symbolSize: responsive.pointSize,
      z: 4,
      label: {
        show: responsive.showPointLabels && config.labels?.show !== false,
        formatter: (params: any) => this.truncateLabel(params.data?.customerName || '', responsive.labelMaxChars),
        position: config.labels?.position || 'right',
        color: config.labels?.color || '#111827',
        fontSize: responsive.pointLabelFontSize,
        distance: responsive.pointLabelDistance,
      },
      labelLayout: {
        hideOverlap: true,
        moveOverlap: 'shiftY',
      },
      markLine: this.buildOpportunityArrows(points as any[], config),
    };
  }

  private resolveCurrentZoom(config: any): { start: number; end: number } {
    const configuredZoom = {
      start: Number(config.start ?? 0),
      end: Number(config.end ?? 100),
    };
    const option = this.myChart?.getOption() as any;
    const activeZoom = Array.isArray(option?.dataZoom)
      ? option.dataZoom.find((item: any) => item?.start !== undefined && item?.end !== undefined)
      : undefined;

    return activeZoom
      ? { start: Number(activeZoom.start), end: Number(activeZoom.end) }
      : configuredZoom;
  }

  toggleLegendCategory(category: string): void {
    this.selectedLegendCategory = this.selectedLegendCategory === category ? '' : category;
    this.renderChart();
  }

  private buildOpportunityArrows(points: any[], config: any): any {
    const arrowConfig = config.opportunityMarkers || {};
    const responsive = this.resolveResponsiveChartConfig(config);
    const maxX = Math.max(...points.map((point) => Number(point.value?.[0] || 0)), 1);
    const data = points
      .filter((point) => point.isOpportunity && point.customerId === this.selectedOpportunityCustomerId)
      .map((point) => [
        { coord: [point.value[0], point.value[1]], symbol: 'none' },
        { coord: [point.value[0], this.thresholdY(point.value[0], 0, maxX)], symbol: arrowConfig.symbol || 'arrow' },
      ]);

    return {
      silent: true,
      symbol: ['none', arrowConfig.symbol || 'arrow'],
      lineStyle: {
        color: arrowConfig.color || '#111827',
        width: responsive.arrowWidth,
        type: arrowConfig.style || 'dashed',
      },
      data,
    };
  }

  private buildOpportunityBadge(config: any): any {
    const badge = config.opportunityBadge;
    if (!badge?.show) return undefined;
    const responsive = this.resolveResponsiveChartConfig(config);
    return {
      symbol: badge.symbol || 'roundRect',
      symbolSize: responsive.badgeSize,
      data: [
        {
          name: badge.label || 'Opportunity',
          coord: [responsive.badgeX ?? badge.x ?? 16, responsive.badgeY ?? badge.y ?? 28],
          label: {
            show: true,
            formatter: badge.label || 'Opportunity',
            color: badge.textColor || '#ffffff',
            fontWeight: 700,
            fontSize: responsive.badgeFontSize,
          },
          itemStyle: {
            color: badge.color || '#6b8e23',
            borderColor: badge.borderColor || '#3f4f11',
            borderWidth: badge.borderWidth || 2,
          },
        },
      ],
    };
  }

  private buildPoints(rows: CustomerPricingRow[]): CustomerPricingPoint[] {
    const customerKey = this.cfg?.dataModel?.customerKey || 'customerId';
    const groups = new Map<string, CustomerPricingRow[]>();
    rows.forEach((row) => {
      if (!row.customerName || !row.customerType) return;
      const key = String(row[customerKey] || row.customerName);
      groups.set(key, [...(groups.get(key) || []), row]);
    });

    return Array.from(groups.values()).map((group) => {
      const base = group[0];
      const serials = new Set(group.map((row) => row.serialNumber).filter(Boolean));
      const aggregate: any = {
        ...base,
        salesQuantity: this.sum(group, 'salesQuantity'),
        listPrice: this.sum(group, 'listPrice'),
        transactionPrice: this.sum(group, 'transactionPrice'),
        currentVolume: this.sum(group, 'currentVolume'),
        futureVolume: this.sum(group, 'futureVolume'),
        projectedSales: this.sum(group, 'projectedSales'),
        distinctSerialCount: serials.size || this.sum(group, 'salesQuantity'),
      };

      const formulas = this.cfg?.formulas || {};
      Object.keys(formulas).forEach((key) => {
        aggregate[key] = this.evaluateFormula(formulas[key], aggregate);
      });
      const xMetric = this.cfg?.customerOverview?.axes?.x?.metric || 'distinctSerialCount';
      const xMax = Math.max(...rows.map((row) => Number(row[xMetric] || 0)), 1);
      aggregate.priceGapVsRegression = Number(aggregate.customerDiscountPct || 0) - this.thresholdY(Number(aggregate[xMetric] || 0), 0, xMax);
      aggregate.potentialUplift = Number(aggregate.priceGapVsRegression || 0) > 0
        ? (Number(aggregate.priceGapVsRegression) / 100) * Number(aggregate.listPrice || 0)
        : 0;
      aggregate.isOpportunity = aggregate.priceGapVsRegression > (this.cfg?.businessRules?.opportunityMinGap || 0);
      return aggregate as CustomerPricingPoint;
    });
  }

  private tooltip(point: CustomerPricingPoint, config: any): string {
    if (!point?.customerId) return '';
    const fields = config.tooltip?.fields || [];
    return fields
      .map((field: any) => `<div><strong>${field.label}</strong>: ${this.formatMetric(point[field.key], field)}</div>`)
      .join('');
  }

  formatMetric(value: any, config: any = {}): string {
    const numeric = Number(value || 0);
    if (config.format === 'currency') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: config.currency || 'USD', maximumFractionDigits: config.decimals ?? 0 }).format(numeric);
    }
    if (config.format === 'percent') {
      return `${numeric.toFixed(config.decimals ?? 1)}${config.suffix || '%'}`;
    }
    return `${numeric.toLocaleString('en-US', { maximumFractionDigits: config.decimals ?? 0 })}${config.suffix || ''}`;
  }

  private evaluateFormula(expression: string, values: Record<string, any>): number {
    if (!expression) return 0;
    const names = Object.keys(values);
    const args = names.map((name) => Number(values[name]) || values[name]);
    try {
      return Number(new Function(...names, 'max', 'min', 'abs', `return ${expression};`)(...args, Math.max, Math.min, Math.abs)) || 0;
    } catch {
      return 0;
    }
  }

  private thresholdY(x: number, minX = 0, maxX?: number): number {
    const line = this.cfg?.customerOverview?.thresholdLine || {};
    if (line.mode === 'domainFit') {
      const startY = Number(line.startY ?? line.intercept ?? 0);
      const endY = Number(line.endY ?? startY);
      const domainMax = Math.max(Number(maxX ?? this.cfg?.customerOverview?.axes?.x?.max ?? 1), minX + 1);
      const ratio = (Number(x || 0) - minX) / (domainMax - minX);
      return startY + (endY - startY) * ratio;
    }
    return Number(line.intercept || 0) + Number(line.slope || 0) * Number(x || 0);
  }

  private resolveColor(category: string, index: number): string {
    const colors = this.cfg?.theme?.categoryColors || {};
    const palette = this.cfg?.theme?.palette || ['#3f3f46', '#0369a1', '#16a34a', '#dc2626'];
    const configured = colors[category] || palette[index % palette.length];
    return this.wrapper.applyColorConsistency(category, configured) || configured;
  }

  private sum(rows: CustomerPricingRow[], key: string): number {
    return rows.reduce((sum, row) => sum + Number(row[key] || 0), 0);
  }

  private extractRows(response: any, dataKey: string): any[] {
    const source =
      (Array.isArray(response) && response) ||
      (Array.isArray(response?.value) && response.value) ||
      (Array.isArray(response?.data) && response.data) ||
      response?.data?.[dataKey] ||
      response?.[dataKey] ||
      response?.responseList?.[0]?.data?.[0]?.[dataKey] ||
      response?.responseList?.[0]?.data ||
      [];

    return Array.isArray(source) ? source : [];
  }

  private normalizeRow(row: any): CustomerPricingRow {
    const mapping = this.cfg?.dataModel?.fieldMap || {};
    const get = (targetKey: string, fallbackKey: string, fallbackValue: any = '') =>
      row?.[mapping[targetKey] || fallbackKey] ?? row?.[targetKey] ?? fallbackValue;
    const customerName = this.cleanText(get('customerName', 'customerName'));
    const listPrice = Number(get('listPrice', 'listPrice', 0)) || 0;
    const transactionPrice = Number(get('transactionPrice', 'transactionPrice', 0)) || 0;
    const configuredDiscount = Number(get('customerDiscountPct', 'customerDiscountPct', NaN));
    const discountPct = Number.isFinite(configuredDiscount)
      ? configuredDiscount <= 1 ? configuredDiscount * 100 : configuredDiscount
      : listPrice ? ((listPrice - transactionPrice) / listPrice) * 100 : 0;

    return {
      ...row,
      customerId: String(get('customerId', 'customerId', customerName)),
      customerName,
      region: this.cleanText(get('region', 'region', 'All Regions')),
      customerType: this.cleanText(get('customerType', 'customerType', 'Customer')) || 'Customer',
      equipment: this.cleanText(get('equipment', 'equipment', '')),
      engine: this.cleanText(get('engine', 'engine', '')),
      serialNumber: this.cleanText(get('serialNumber', 'serialNumber', '')),
      salesQuantity: Number(get('salesQuantity', 'salesQuantity', 0)) || 0,
      listPrice,
      transactionPrice,
      currentVolume: Number(get('currentVolume', 'currentVolume', 0)) || 0,
      futureVolume: Number(get('futureVolume', 'futureVolume', 0)) || 0,
      projectedSales: Number(get('projectedSales', 'projectedSales', 0)) || 0,
      customerDiscountPct: discountPct,
    };
  }

  private cleanText(value: any): string {
    return String(value ?? '').replace(/^\uFEFF|^ï»¿/, '').trim();
  }

  private unique(values: any[]): string[] {
    return Array.from(new Set(values.map((value) => String(value))));
  }

  private initializeFilterState(): void {
    return;
  }

  private ensureResizeObserver(): void {
    if (this.resizeObserver || !this.chartHost?.nativeElement) return;
    const target = this.chartHost.nativeElement;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeRafId !== null) {
        cancelAnimationFrame(this.resizeRafId);
      }
      this.resizeRafId = requestAnimationFrame(() => {
        this.resizeRafId = null;
        this.myChart?.resize();
      });
    });
    this.resizeObserver.observe(target);
  }

  private resolveResponsiveChartConfig(config: any): any {
    const width = this.chartHost?.nativeElement?.clientWidth || 900;
    const compact = width < 760;
    const narrow = width < 560;
    const veryNarrow = width < 430;
    const scale = Math.max(0.62, Math.min(1, width / 980));
    const baseGrid = config.grid || {};

    return {
      grid: {
        left: veryNarrow ? 38 : narrow ? 44 : compact ? 50 : baseGrid.left ?? 62,
        right: veryNarrow ? 12 : narrow ? 16 : compact ? 22 : baseGrid.right ?? 34,
        top: veryNarrow ? 28 : narrow ? 30 : compact ? 32 : baseGrid.top ?? 34,
        bottom: veryNarrow ? 34 : narrow ? 36 : compact ? 38 : baseGrid.bottom ?? 42,
        containLabel: true,
      },
      legendTop: veryNarrow ? 0 : narrow ? 0 : compact ? 2 : config.legend?.top ?? 2,
      legendItemSize: Math.round(18 * scale),
      legendGap: Math.round(22 * scale),
      legendFontSize: Math.round(17 * scale),
      axisLabelFontSize: Math.round(15 * scale),
      axisNameFontSize: Math.round(16 * scale),
      xAxisNameGap: veryNarrow ? 22 : narrow ? 24 : compact ? 28 : config.axes?.x?.nameGap ?? 30,
      pointSize: Math.round((config.point?.size || 24) * scale),
      pointLabelFontSize: Math.round(16 * scale),
      pointLabelDistance: Math.round(8 * scale),
      pointLabelWidth: veryNarrow ? 0 : narrow ? 72 : compact ? 90 : 130,
      showPointLabels: !veryNarrow,
      labelMaxChars: veryNarrow ? 0 : narrow ? 10 : compact ? 12 : 18,
      arrowWidth: Math.max(1, Math.round((config.opportunityMarkers?.width || 2) * scale)),
      badgeSize: [Math.round(122 * scale), Math.round(54 * scale)],
      badgeFontSize: Math.round(16 * scale),
      badgeX: narrow ? 15 : undefined,
      badgeY: narrow ? 29 : undefined,
    };
  }

  private truncateLabel(value: string, maxChars: number): string {
    if (!maxChars || value.length <= maxChars) return value;
    return `${value.slice(0, Math.max(1, maxChars - 1))}…`;
  }

  private buildPointLabel(point: any, index: number, xMetric: string, yMetric: string, responsive: any, config: any): any {
    if (!responsive.showPointLabels || config.labels?.show === false) {
      return { show: false };
    }

    const crowded = this.isCrowdedPoint(point, xMetric, yMetric);
    const positions = crowded
      ? ['right', 'left', 'top', 'bottom']
      : ['right', 'top', 'left', 'bottom'];
    const position = positions[index % positions.length];

    return {
      show: true,
      formatter: (params: any) => this.truncateLabel(params.data?.customerName || '', responsive.labelMaxChars),
      position,
      distance: crowded ? responsive.pointLabelDistance + 6 : responsive.pointLabelDistance,
      color: config.labels?.color || '#111827',
      fontSize: responsive.pointLabelFontSize,
      lineHeight: Math.round(responsive.pointLabelFontSize * 1.15),
      overflow: 'truncate',
      width: responsive.pointLabelWidth,
    };
  }

  private isCrowdedPoint(point: any, xMetric: string, yMetric: string): boolean {
    const x = Number(point[xMetric] || 0);
    const y = Number(point[yMetric] || 0);
    return this.filteredPoints.some((other) => {
      if (other.customerId === point.customerId) return false;
      const otherX = Number(other[xMetric] || 0);
      const otherY = Number(other[yMetric] || 0);
      return Math.abs(otherX - x) <= 3 && Math.abs(otherY - y) <= 5;
    });
  }
}
